<template>

  <!-- 技术支持导航菜单区域 -->
  <div class="support-menu" v-if="pluginId === admin">
    <div class="support-options">
      <div v-for="(item, index) in supportItems" :key="index" class="support-item">
        <el-icon><component :is="item.icon" /></el-icon>
        <div class="support-title">{{ item.title }}</div>
      </div>
    </div>
  </div>

  <div class="plugin-details-container">
    <!-- 顶部横幅 -->
    <div class="top-banner">
      <div class="banner-overlay">
        <div class="banner-content">
          <h1 class="plugin-title">{{ plugin.name }}</h1>
          <p class="plugin-subtitle">{{ plugin.description }}</p>
          <div class="banner-actions">
            <el-button
                v-if="pluginId === 1 || pluginId === 6"
                type="primary"
                size="large"
                class="download-btn"
                @click="handleTrialDownload"
                :loading="downloadLoading">
              <el-icon><Download /></el-icon>
              立即下载试用
            </el-button>
            <el-button size="large" class="learn-more-btn">
              了解功能
            </el-button>

<!--            <el-button @click="goToVersions" type="text" class="version-link">-->
<!--               <el-icon><History /></el-icon>-->
<!--               查看所有版本-->
<!--            </el-button>-->

          </div>
        </div>
      </div>
    </div>

    <!-- 导航菜单 -->
    <div class="nav-menu" ref="navMenu">
      <div class="nav-container">
        <div class="nav-items">
          <div
              v-for="(item, index) in navItems"
              :key="index"
              class="nav-item"
              :class="{ active: activeNav === item.key }"
              @click="scrollToSection(item.key)"
          >
            {{ item.label }}
          </div>
        </div>
        <div class="nav-actions">
          <el-button v-if="pluginId === 1 || pluginId === 6"  type="primary" class="buy-now-btn" @click="goToPricing">
            立即购买
          </el-button>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 概述部分 -->
      <section id="overview" class="content-section">
        <div class="section-header">
          <h2>插件概述</h2>
          <div class="section-divider"></div>
        </div>
        <div class="overview-content">
          <div class="overview-video">
            <div class="video-container">
              <!-- poster属性表示的是视频的封面，真正调用的内容是plugin.media -->
              <video
                  :poster="plugin.image"
                  class="main-video"
              >
                <source :src="plugin.media" type="video/mp4">
                您的浏览器不支持视频播放。
              </video>
            </div>
          </div>
          <div class="overview-features">
            <div class="features-grid">
              <div v-for="(feature, index) in plugin.coreFeatures" :key="index" class="feature-card">
                <div class="feature-icon">
                  <el-icon><component :is="feature.icon" /></el-icon>
                </div>
                <div class="feature-content">
                  <h4>{{ feature.title }}</h4>
                  <p>{{ feature.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 功能特性部分 -->
      <section id="features" class="content-section">
        <div class="section-header">
          <h2>功能特性</h2>
          <div class="section-divider"></div>
        </div>
        <div class="features-detail">
          <div class="feature-detail-item" v-for="(feature, index) in plugin.detailedFeatures" :key="index">
            <div class="feature-detail-content" :class="{ 'reverse': index % 2 === 1 }">
              <div class="feature-text">
                <h3>{{ feature.title }}</h3>
                <p>{{ feature.description }}</p>
                <ul v-if="feature.items">
                  <li v-for="(item, itemIndex) in feature.items" :key="itemIndex">{{ item }}</li>
                </ul>
              </div>
              <div class="feature-visual">
                <el-image
                    :src="feature.image"
                    :alt="feature.title"
                    :preview-src-list="[feature.image]"
                    fit="cover"
                    style="width: 100%; border-radius: 12px; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 应用场景部分 -->
      <section id="applications" class="content-section">
        <div class="section-header">
          <h2>适用行业与场景</h2>
          <div class="section-divider"></div>
        </div>
        <div class="applications-grid">
          <div v-for="(app, index) in plugin.applications" :key="index" class="application-card">
            <div class="app-icon">
              <el-icon><component :is="app.icon" /></el-icon>
            </div>
            <h4>{{ app.title }}</h4>
            <p>{{ app.description }}</p>
          </div>
        </div>
      </section>

      <!-- 技术规格部分 -->
      <section id="specifications" class="content-section">
        <div class="section-header">
          <h2>技术规格</h2>
          <div class="section-divider"></div>
        </div>
        <div class="specs-content">
          <div class="specs-list">
            <div class="spec-item" v-for="(spec, index) in plugin.specifications" :key="index">
              <div class="spec-label">{{ spec.label }}</div>
              <div class="spec-value">{{ spec.value }}</div>
            </div>
          </div>
          <div class="specs-visual">
            <img :src="plugin.specImage" alt="技术规格示意图" />
          </div>
        </div>
      </section>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, markRaw, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import QRCode from 'qrcode'
import request from '@/utils/request'
import { ElMessage } from 'element-plus'
import {
  ShoppingCart, Star, Download, Collection,
  Monitor, Clock, Document, ChatDotRound, Loading,
  Phone, VideoCamera, VideoPlay, Headset, ArrowLeft,
  Check, Printer, Setting, TrendCharts, Picture, Files,
} from '@element-plus/icons-vue'

// 获取路由参数中的插件ID
const route = useRoute()
const router = useRouter()
const pluginId = ref(parseInt(route.query.id))

const downloadLoading = ref(false)

const pollInterval = ref(null)

// 立即购买按钮点击事件
const goToPricing = () => {
  router.push({
    path: '/productPricing',
    query: { id: pluginId.value }
  })
}

// 跳转到教程页面
const goToTutorial = () => {
  // 根据插件ID生成对应的教程路径名称
  let tutorialPath = '';

  switch (pluginId.value) {
    case 1:
      tutorialPath = 'ebistudy';
      break;
    case 2:
      tutorialPath = 'qrcodetutorial';
      break;
    case 3:
      tutorialPath = 'cuttingmodeltutorial';
      break;
    case 4:
      tutorialPath = 'energyefficiencytutorial';
      break;
    case 5:
      tutorialPath = 'arrowpositiontutorial';
      break;
    case 6:
      tutorialPath = 'ebitututorial';
      break;
    default:
      tutorialPath = 'default';
  }

  router.push(`/tutorial/${tutorialPath}`);
}

// 添加获取最新版本的方法
const downloadLatestVersion = async () => {
  downloadLoading.value = true

  try {
    // 先获取产品编码
    const productResponse = await request.get(`/plugin/details/${pluginId.value}`)

    if (productResponse.code === '200' && productResponse.data) {
      const productCode = productResponse.data.productCode

      if (!productCode) {
        ElMessage.error('无法获取产品编码')
        return
      }

      // 记录下载行为
      await recordTrialDownload()

      // 调用最新版本下载接口
      window.location.href = `/api/file/download-latest/${productCode}`

    } else {
      ElMessage.error('获取产品信息失败')
    }

  } catch (error) {
    console.error('下载处理失败:', error)
    ElMessage.error('下载失败')
  } finally {
    downloadLoading.value = false
  }
}

// 添加试用下载处理方法
// const handleTrialDownload = async () => {
//   downloadLoading.value = true
//
//   try {
//     // 先记录下载行为
//     await recordTrialDownload()
//
//     // 然后执行原有的下载逻辑
//     window.location.href = '/api/downPlugin/download-exe'
//
//   } catch (error) {
//     console.error('下载处理失败:', error)
//     // 即使记录失败，也允许用户下载
//     window.location.href = '/api/downPlugin/download-exe'
//   } finally {
//     // 注意：这里不重置loading状态，因为页面会跳转
//     // downloadLoading.value = false
//   }
// }

const handleTrialDownload = async () => {
  await downloadLatestVersion()
}

// 添加跳转到版本列表的方法
const goToVersions = () => {
  if (plugin.value && plugin.value.productCode) {
    router.push({
      path: '/pluginVersions',
      query: {
        code: plugin.value.productCode,
        name: plugin.value.name
      }
    })
  } else {
    ElMessage.warning('无法获取产品编码')
  }
}

// 记录试用下载
const recordTrialDownload = async () => {
  try {
    const response = await request.post(`/plugin/record/download?productId=${pluginId.value}`);

    if (response.code === '200') {
      console.log('试用下载记录成功')
    } else {
      console.warn('试用下载记录失败:', response.message)
    }
  } catch (error) {
    console.error('记录试用下载失败:', error)
    // 这里不抛出错误，让下载继续执行
  }
}

// 监听路由变化，更新插件ID
watch(() => route.query.id, (newId) => {
  if (newId) {
    pluginId.value = parseInt(newId)
    // const newPluginId = parseInt(newId)
    // const pluginExists = allPlugins.value.some(p => p.id === newPluginId)
    //
    // if (!pluginExists) {
    //   console.log("插件不存在")
    //   router.push('/home')
    //   return
    // }

    // pluginId.value = newPluginId
  }
})

// 定义所有插件数据
const allPlugins = ref([
  {
    id: 1,
    name: '易比图--像素级图片比对，可调参数高亮差异点',
    description: '让差异无处遁形，一眼发现不同！',

    image: 'https://test.eprinttools.com/files/front-end/yibi.gif',

    media: 'https://test.eprinttools.com/files/front-end/yibi.gif',
    // 技术规格图片
    specImage: 'https://test.eprinttools.com/files/front-end/spec/specYi.png',
    // 核心功能
    coreFeatures: [
      {
        icon: markRaw(Printer),
        title: '一键智能对比',
        description: '快速对比多组图片，自动识别并高亮显示所有差异区域'
      },
      {
        icon: markRaw(Picture),
        title: '像素级精准检测',
        description: '采用先进算法实现像素级差异识别，确保检测精度达到99.9%'
      },
      {
        icon: markRaw(TrendCharts),
        title: '多格式全面兼容',
        description: '支持JPG、PNG、PDF等主流图片格式，满足多样化文件处理需求'
      },
      {
        icon: markRaw(Files),
        title: '批量高效处理',
        description: '支持批量对比任务，大幅提升图片校对和质检工作效率'
      }
    ],
    // 详细功能特性
    detailedFeatures: [
      {
        title: '操作效率与辅助工具',
        description: '快捷键与系统级功能支持',
        items: [
          '图像区域标注 快捷键(Alt + Q)：通过键盘快捷键触发图像上传、替换等操作，提升批量处理效率',
          '顶部 系统设置(F)/ 帮助(H)入口：快速访问全局参数配置、使用指南等内容，降低工具学习成本'
        ],
        image: 'https://test.eprinttools.com/files/front-end/features/yibifeat0.gif',
      },
      {
        title: '图像对比检测核心流程',
        description: '支持多图输入与差异识别全流程操作',
        items: [
          '提供了至少两个图像交互区域（截图快捷键可自动替换），用于上传/展示待对比的原图与目标图',
          '设有 开始检测按钮，触发图像间的差异比对任务',
          '支持通过界面控件（如滑块、下拉框）调整差异识别的核心参数，实现精准度与识别逻辑的灵活控制'
        ],
        image: 'https://test.eprinttools.com/files/front-end/features/yibifeat1.gif',
      },
      {
        title: '差异识别与标记配置',
        description: '自定义差异判定规则与视觉标识样式',
        items: [
          '识别差异下拉选项（当前显示“2像素”）：设置差异尺寸的过滤阈值，仅识别指定大小以上的差异区域',
          '标记颜色下拉选项（当前显示“红色”）：选择差异区域的标注色彩，满足个性化视觉区分需求',
          '差异阈值滑块（范围0 - 255）：通过数值化调节控制差异敏感度，适配不同亮度、对比度的图像比对场景',
          '校对模式（默认勾选一比一模式）：通过格式、布局校对或图文信息校对的不同的需求来应用不同的校对模式'
        ],
        image: 'https://test.eprinttools.com/files/front-end/features/yibifeat2.gif',
      },
      {
        title: '结果呈现与反馈',
        description: '可视化与文字化双维度结果展示',
        items: [
          '差异结果区域：直观呈现检测到的图像差异位置及标记样式（如颜色标注）',
          '校对报告文本框：自动生成文字版比对结论，包含图片阀值，差异数量等信息，便于报告存档与团队协作'
        ],
        image: 'https://test.eprinttools.com/files/front-end/features/yibifeat3.gif',
      }
    ],
    // 应用场景
    applications: [
      {
        icon: markRaw(Printer),
        title: '印刷包装质检',
        description: '检测印刷品与设计稿的差异，确保色彩、位置、内容的准确性'
      },
      {
        icon: markRaw(Setting),
        title: '制造过程检验',
        description: '对比产品样品与标准图纸，发现制造过程中的偏差和缺陷'
      },
      {
        icon: markRaw(TrendCharts),
        title: '科研实验分析',
        description: '分析实验前后图像变化，为科学研究提供精确的数据支持'
      },
      {
        icon: markRaw(Monitor),
        title: '软件UI测试',
        description: '自动化对比UI界面版本差异，确保软件更新的一致性'
      },
      {
        icon: markRaw(Picture),
        title: '安防取证分析',
        description: '比对监控图像变化，为安全调查提供可靠的视觉证据'
      },
      {
        icon: markRaw(Files),
        title: '医疗影像诊断',
        description: '辅助医生对比治疗前后影像，跟踪病情发展和治疗效果'
      }
    ],
    // 技术规格
    specifications: [
      { label: '插件版本', value: '1.2.20' },
      { label: '软件要求', value: 'Adobe Illustrator CC 2019+' },
      { label: '操作系统', value: 'Windows 10/11, macOS 10.15+' },
      { label: '支持格式', value: 'JPG, PNG, PDF' },
      { label: '检测精度', value: '像素级，精度达99.9%' },
      { label: '授权方式', value: '订阅授权' }
    ]
  },
  {
    id: 6,
    name: '易比图 Pro -- 智能多格式图像比对软件',
    description: '支持PDF/图片多格式比对，差异点可视化追踪，让每一次改动都清晰可见！',

    // 主展示图可替换为展示PDF+图片混合对比的GIF
    image: 'https://test.eprinttools.com/files/front-end/ebt1002.gif',
    media: 'https://test.eprinttools.com/files/front-end/ebt1002.gif',

    // 技术规格图：展示PDF校对+缩略图面板
    specImage: 'https://test.eprinttools.com/files/front-end/spec/specYi.png',

    // 核心功能（更新版）
    coreFeatures: [
      {
        icon: markRaw(Printer),
        title: 'PDF与多格式混合校对',
        description: '支持PDF、JPG、PNG等格式同屏比对，自动解析PNG图片进行像素级分析'
      },
      {
        icon: markRaw(Picture),
        title: '差异点缩略图导航',
        description: '每个检测到的差异点自动生成缩略图，点击即可快速定位并查看细节对比'
      },
      {
        icon: markRaw(TrendCharts),
        title: '动态差异结果展示',
        description: '生成GIF动态对比图，直观呈现修改前后的变化过程，支持逐帧查看'
      },
      {
        icon: markRaw(Files),
        title: '尺寸差异处理',
        description: '支持不同尺寸的图片序列的自动比对，输出结构化差异结果'
      }
    ],

    // 详细功能特性（重构）
    detailedFeatures: [
      {
        title: '多格式文件支持',
        description: 'PDF文件与PNG图片进行同屏校对',
        items: [
          'PDF文件自动解析：支持PDF文件导入，自动拆分PNG图像进行序列比对',
          '混合格式同屏比对：支持PDF页面与JPG/PNG图片在同一次任务中进行交叉比对',
          '智能预处理：自动统一不同格式文件的色彩模式、分辨率与尺寸，确保比对基准一致'
        ],
        image: 'https://test.eprinttools.com/files/front-end/features/ebt1002feat0.gif',
      },
      {
        title: '差异检测与缩略图导航系统',
        description: '像素级检测+视觉化差异点管理',
        items: [
          '差异点聚类算法：智能合并相邻差异像素，生成有意义的差异区域而非散点',
          '缩略图面板：右侧独立面板展示所有差异点缩略图，标注序号与差异程度',
          '一键定位导航：点击缩略图自动定位至画面对应位置，并高亮显示差异边界框'
        ],
        image: 'https://test.eprinttools.com/files/front-end/features/ebt1002feat1.gif',
      },
      {
        title: '动态结果生成与参数微调',
        description: 'GIF生成与敏感度精准控制',
        items: [
          'GIF动态对比模式：自动生成前后对比动画，可调节播放速度（慢/中/快）',
          '差异敏感度矩阵控制：提供色差、亮度、结构相似度(SSIM)多维参数独立调节',
          'PDF特定参数：针对PDF文本比对提供“文字轮廓模式”，忽略排版位移专注内容比对',
          '差异过滤规则：支持按面积、颜色、形状等多维度过滤不重要的细微差异'
        ],
        image: 'https://test.eprinttools.com/files/front-end/features/ebt1002feat2.gif',
      }
    ],

    // 应用场景（更新描述以匹配新功能）
    applications: [
      {
        icon: markRaw(Printer),
        title: '印刷品与PDF校对',
        description: '对比印刷稿与PDF源文件的差异，确保文字、图形、色彩完全一致'
      },
      {
        icon: markRaw(Setting),
        title: '多版本文档审核',
        description: '快速比对合同、报告等PDF文档的不同版本，定位修改内容与位置'
      },
      {
        icon: markRaw(TrendCharts),
        title: 'UI/UX设计版本追踪',
        description: '通过动态对比图清晰展示设计迭代过程，直观呈现每处界面改动'
      },
      {
        icon: markRaw(Monitor),
        title: '数字内容质检',
        description: '对游戏素材、电商图片进行批量自动化比对，确保多平台内容一致性'
      },
      {
        icon: markRaw(Picture),
        title: '学术出版校对',
        description: '精确比对论文图表、数据可视化在不同版本中的细微变化'
      },
      {
        icon: markRaw(Files),
        title: '法律证据分析',
        description: '对比证据材料图像与扫描件，生成带有定位标记的可视化差异报告'
      }
    ],

    // 技术规格（更新）
    specifications: [
      { label: '插件版本', value: '2.5.7' },
      { label: '软件要求', value: '独立应用程序' },
      { label: '操作系统', value: 'Windows 10/11, macOS 11.0+' },
      { label: '支持格式', value: 'PDF、JPG、PNG' },
      { label: '输出格式', value: '动态GIF、标注图像' },
      { label: '授权方式', value: '订阅式' }
    ]
  },
  {
    id: 2,
    name: '企业级二维码生成器--智能编码与品牌管理',
    description: '支持多种二维码标准与定制化生成，满足企业多样化编码需求',
    price: 29,
    sales: 1284,
    rating: 4.7,
    image: 'https://test.eprinttools.com/files/front-end/code.gif',
    type: '编码工具',
    software: 'Adobe Illustrator',
    author: '编码技术实验室',
    authorAvatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80',
    tag: '热门',
    tagColor: '#ff4d4f',
    media: 'https://test.eprinttools.com/files/front-end/code.gif',
    // 技术规格图片
    specImage: 'https://test.eprinttools.com/files/front-end/spec/specCode.png',
    // 核心功能
    coreFeatures: [
      {
        icon: markRaw(Printer),
        title: '多标准支持',
        description: '支持40/41二维码、TCL二维码、海信二维码等多种行业标准格式'
      },
      {
        icon: markRaw(Picture),
        title: '智能参数配置',
        description: '自定义印刷厂家、物料编码，灵活配置二维码参数和尺寸'
      },
      {
        icon: markRaw(TrendCharts),
        title: '一键生成',
        description: 'AI内直接生成可编辑矢量二维码，支持批量处理和导出'
      },
      {
        icon: markRaw(Files),
        title: '企业级定制',
        description: '支持品牌定制、LOGO嵌入，满足企业专属编码需求'
      }
    ],
    // 详细功能特性
    detailedFeatures: [
      {
        title: '多业务场景标签页切换',
        description: '覆盖不同厂商/类型的二维码生成需求',
        items: [
          '界面顶部设置多标签页导航栏，包含「海信24位条码生成」「TCL二维码」「41二维码」「40二维码」等选项，精准适配不同厂商（如海信、TCL）或通用二维码的生成场景',
          '点击标签页可快速切换至对应业务界面，界面元素（如输入字段、参数选项）随标签内容动态调整，实现「场景化生成」',
          '兼容国际主流编码标准'
        ],
        image: 'https://test.eprinttools.com/files/front-end/features/codefeat1.gif',
      },
      {
        title: '定制化输入字段配置',
        description: '用户可自定义印刷厂家信息和物料编码，生成专属二维码',
        items: [
          '厂商/印刷厂类字段，适配不同厂商的编码前缀或归属规则',
          '编号/物料类字段，满足个性化产品编号需求',
          '实时预览生成效果'
        ],
        image: 'https://test.eprinttools.com/files/front-end/features/codefeat2.gif',
      },
      {
        title: '二维码参数灵活调整',
        description: '支持物理尺寸等生成参数自定义',
        items: [
          '部分标签页（如「41二维码」）设置二维码大小单选组，默认选中「50x50mm」，可选「30x30mm」，用户可根据标签打印尺寸、使用场景调整二维码物理大小',
          '参数与输入内容联动，调整后实时映射至二维码生成结果，无需额外操作',
          '界面视觉：整体采用浅灰色/浅色背景，控件（下拉框、单选按钮、输入框）排列规整，降低用户认知负担，提升生成效率'
        ],
        image: 'https://test.eprinttools.com/files/front-end/features/codefeat3.gif',
      }
    ],
    // 应用场景
    applications: [
      {
        icon: markRaw(Files),
        title: '产品标识',
        description: '为各类产品生成唯一标识二维码，实现产品溯源和管理'
      },
      {
        icon: markRaw(Setting),
        title: '供应链管理',
        description: '物料编码与供应商信息整合，提升供应链可视化'
      },
      {
        icon: markRaw(TrendCharts),
        title: '品牌防伪',
        description: '生成品牌专属二维码，防止假冒伪劣产品'
      },
      {
        icon: markRaw(Monitor),
        title: '库存管理',
        description: '通过二维码实现库存精准管理和快速盘点'
      }
    ],
    // 技术规格
    specifications: [
      { label: '插件版本', value: '3.0.1' },
      { label: '软件要求', value: 'Adobe Illustrator CC 2019+' },
      { label: '操作系统', value: 'Windows 10/11, macOS 10.15+' },
      { label: '文件格式', value: 'AI, EPS, PDF, SVG' },
      { label: '语言支持', value: '中文, English, 日本語' },
      { label: '授权方式', value: '订阅制' }
    ]
  },
  {
    id: 3,
    name: '智能刀模生成插件--让包装设计效率飞升',
    description: '输入尺寸自动生成精准刀模线，支持复杂盒型设计',
    price: 49,
    sales: 985,
    rating: 4.8,
    image: 'https://test.eprinttools.com/files/front-end/mo.gif',
    type: '设计工具',
    software: 'Adobe Illustrator',
    author: '包装专家团队',
    authorAvatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80',
    tag: '新品',
    tagColor: '#52c41a',
    media: 'https://test.eprinttools.com/files/front-end/mo.gif',
    // 技术规格图片
    specImage: 'https://test.eprinttools.com/files/front-end/spec/specMo.png',
    // 核心功能
    coreFeatures: [
      {
        icon: markRaw(Printer),
        title: '秒级生成',
        description: '输入盒型尺寸自动输出标准刀模线，设计耗时缩短90%'
      },
      {
        icon: markRaw(Picture),
        title: '精准适配生产',
        description: '内置出血位、压痕线智能校准，避免设计误差'
      },
      {
        icon: markRaw(TrendCharts),
        title: '智能拼版',
        description: '自动优化刀模排列，节省15%~30%材料成本'
      },
      {
        icon: markRaw(Files),
        title: '盒型库丰富',
        description: '支持FEFCO国际标准及自定义异形结构'
      }
    ],
    // 详细功能特性
    detailedFeatures: [
      {
        title: '基础视觉样式设置',
        description: '支持刀模文字与线条属性自定义',
        items: [
          '字体参数调节：可设置刀模关联文字（如标识、规格说明等）的字体大小，满足不同印刷清晰度与设计风格需求',
          '线粗参数调节：能调整刀模切割线条的粗细程度，适配不同材质（如卡纸、硬纸板）的切割精度与成型效果要求'
        ],
        image: 'https://test.eprinttools.com/files/front-end/features/mofeat1.gif',
      },
      {
        title: '手孔定制化设计',
        description: '灵活配置天地盖手挽安装孔位参数',
        items: [
          '数量选择：提供下拉选项设定手挽安装孔的数量，匹配单孔、双孔等不同手挽结构与使用场景',
          '间距调整：支持输入数值定义手孔间的水平/垂直间距，确保手挽安装后受力均匀、操作便利',
          '位置设定：通过数值输入精准控制手孔在刀模模板上的坐标位置，适配天地盖模板的特定布局逻辑'
        ],
        image: 'https://test.eprinttools.com/files/front-end/features/mofeat2.gif',
      },
      {
        title: '尺寸参数化驱动',
        description: '可视化调整天地盖各部件尺寸',
        items: [
          '多维度尺寸输入：支持顶部、底部、侧边等多区域的尺寸数值输入（如界面中“200mm”“600mm”类参数），覆盖天地盖整体与局部的尺寸定义',
          '增量尺寸调节：提供“+0”“-0”类增量输入框，支持基于当前尺寸的微调操作，满足包装盒精细化设计需求'
        ],
        image: 'https://test.eprinttools.com/files/front-end/features/mofeat3.gif',
      },
      {
        title: '刀模图形可视化预览',
        description: '呈现天地盖刀模切割效果',
        items: [
          '结构分层展示：清晰显示天地盖各面板（顶盖、底盒、侧翼等）的折叠与切割轮廓，辅助设计师快速检查结构合理性',
          '刀线样式区分：通过不同线条样式（如实线、虚线）区分切割线、折叠线等工艺信息，避免生产环节混淆',
          '全尺寸适配预览：生成的刀模图包含完整尺寸标注（如侧边“200mm”、主体“600mm”等），便于生产端按图精准加工'
        ],
        image: 'https://test.eprinttools.com/files/front-end/mo.gif',
      }
    ],
    // 应用场景
    applications: [
      {
        icon: markRaw(Files),
        title: '包装设计',
        description: '各类产品包装盒型设计，提升设计效率和精度'
      },
      {
        icon: markRaw(Setting),
        title: '印刷制作',
        description: '为印刷厂提供精准刀模文件，确保生产质量'
      },
      {
        icon: markRaw(TrendCharts),
        title: '品牌定制',
        description: '为品牌客户定制专属包装结构方案'
      },
      {
        icon: markRaw(Monitor),
        title: '教育培训',
        description: '包装设计教学和培训，快速掌握盒型结构'
      }
    ],
    // 技术规格
    specifications: [
      { label: '插件版本', value: '2.5.0' },
      { label: '软件要求', value: 'Adobe Illustrator CC 2018+' },
      { label: '操作系统', value: 'Windows 10/11, macOS 10.14+' },
      { label: '支持盒型', value: '50+ 标准盒型，支持自定义' },
      { label: '文件格式', value: 'AI, DXF, PDF' },
      { label: '授权方式', value: '订阅授权' }
    ]
  },
  {
    id: 4,
    name: '智能能效标签管理插件--数据零差错，合规全自动',
    description: '自动获取数据并填充，支持20+国际标准模板',
    price: 39,
    sales: 756,
    rating: 4.6,
    image: 'https://test.eprinttools.com/files/front-end/neng.gif',
    type: '数据工具',
    software: 'Adobe InDesign, Microsoft Excel',
    author: '数据科技公司',
    authorAvatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80',
    tag: '必备',
    tagColor: '#1890ff',
    media: 'https://test.eprinttools.com/files/front-end/neng.gif',
    // 技术规格图片
    specImage: 'https://test.eprinttools.com/files/front-end/spec/specNeng.png',
    // 核心功能
    coreFeatures: [
      {
        icon: markRaw(Printer),
        title: '数据自动化',
        description: 'Excel/ERP直连自动抓取产品参数，避免手动输入错误'
      },
      {
        icon: markRaw(Picture),
        title: '智能格式转换',
        description: '支持欧盟ERP、中国能效标识等20+国际标准模板'
      },
      {
        icon: markRaw(TrendCharts),
        title: '动态参数更新',
        description: '检测报告数值变动时，标签文档自动同步更新'
      },
      {
        icon: markRaw(Files),
        title: '合规性校验',
        description: '内置各国能效法规阈值库，超标参数实时预警'
      }
    ],
    // 详细功能特性
    detailedFeatures: [
      {
        title: '智能定界框定位与绘制',
        description: '通过坐标与定界框实现标签元素精准排版',
        items: [
          '支持输入 X/Y Coordinate坐标值，精准定位标签内各元素（如图形、文本框）的位置',
          '提供 生成定界框功能，快速框选并定义标签区域内元素的边界范围',
          '配备 清除定界框功能，一键重置元素定位布局，便于重新调整排版逻辑'
        ],
        image: 'https://test.eprinttools.com/files/front-end/neng.gif'
      },
      {
        title: '自动化数据提取与参数生成',
        description: '从源文件快速提取并生成标签参数',
        items: [
          '支持从Excel等格式源文件中提取能效核心数据（如 SEER、SCOP、能耗值（kWh/annum）等）',
          '设计 提取1/提取2按钮实现多维度数据抓取，生成参数功能自动将提取数据转化为标签可编辑参数',
          '支持批量数据处理（右侧数据列表展示多组数值，可推测支持多组数据导入与参数批量生成）'
        ],
        image: 'https://test.eprinttools.com/files/front-end/neng.gif'
      }
    ],
    // 应用场景
    applications: [
      {
        icon: markRaw(Files),
        title: '家电制造',
        description: '为各类家电产品生成符合法规的能效标签'
      },
      {
        icon: markRaw(Setting),
        title: '照明产品',
        description: '荧光灯、LED灯等照明产品的能效标识制作'
      },
      {
        icon: markRaw(TrendCharts),
        title: '空调热泵',
        description: '空调、热泵等产品的能效等级标识'
      },
      {
        icon: markRaw(Monitor),
        title: '出口贸易',
        description: '满足不同国家能效标签要求的出口产品'
      }
    ],
    // 技术规格
    specifications: [
      { label: '插件版本', value: '3.0.1' },
      { label: '软件要求', value: 'Adobe InDesign CC 2019+, Excel 2016+' },
      { label: '支持标准', value: '欧盟ERP、中国能效标识等20+标准' },
      { label: '数据源', value: 'Excel, CSV, ERP系统' },
      { label: '更新方式', value: '自动同步' },
      { label: '授权方式', value: '订阅制' }
    ]
  },
  {
    id: 5,
    name: '美国版能效标签三角箭头智能定位插件',
    description: '自动计算三角箭头在能效标尺的准确位置',
    price: 59,
    sales: 632,
    rating: 4.5,
    image: 'https://test.eprinttools.com/files/front-end/huang.gif',
    type: '专业工具',
    software: 'Adobe Illustrator, Adobe Photoshop',
    author: '能效标准实验室',
    authorAvatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80',
    tag: '专业',
    tagColor: '#722ed1',
    media: 'https://test.eprinttools.com/files/front-end/huang.gif',
    // 技术规格图片
    specImage: 'https://test.eprinttools.com/files/front-end/spec/specSan.png',
    // 核心功能
    coreFeatures: [
      {
        icon: markRaw(Printer),
        title: '智能定位',
        description: '自动计算三角箭头在能效标尺的准确位置'
      },
      {
        icon: markRaw(Picture),
        title: '批量处理',
        description: '可批量处理多个产品标签，大幅提升效率'
      },
      {
        icon: markRaw(TrendCharts),
        title: '规范输出',
        description: '确保输出的标签文件完全符合美国能效规范'
      },
      {
        icon: markRaw(Files),
        title: '广泛适用',
        description: '适用于荧光灯、家电、空调、热泵等产品'
      }
    ],
    // 详细功能特性
    detailedFeatures: [
      {
        title: '智能定位计算',
        description: '依据输入的能效范围值，自动算出三角箭头准确位置',
        items: [
          "快速依据输入的能效范围值，自动算出三角箭头在能效标尺的准确位置并调整，无需手动计算比例，确保输出的标签文件符合规范。",
          "相比手工操作，大幅提升工作效率，可批量处理多个产品标签。",
          "适用于各类需制作美国能效标签的产品，如荧光灯、家电、空调、热泵等。无论是制造商、分销商等，都能借助插件轻松完成标签制作与更新。"
        ],
        image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80'
      }
    ],
    // 应用场景
    applications: [
      {
        icon: markRaw(Files),
        title: '荧光灯制造',
        description: '为荧光灯产品生成符合美国标准的能效标签'
      },
      {
        icon: markRaw(Setting),
        title: '家电产品',
        description: '各类家电产品的美国市场能效标识制作'
      },
      {
        icon: markRaw(TrendCharts),
        title: '空调设备',
        description: '空调、热泵等产品的美国能效标签'
      },
      {
        icon: markRaw(Monitor),
        title: '分销商',
        description: '分销商为产品制作符合美国要求的能效标签'
      }
    ],
    // 技术规格
    specifications: [
      { label: '插件版本', value: '1.5.2' },
      { label: '软件要求', value: 'Adobe Illustrator/Photoshop CC 2018+' },
      { label: '适用产品', value: '荧光灯、家电、空调、热泵等' },
      { label: '标准符合', value: '美国能效标签规范' },
      { label: '处理方式', value: '批量处理' },
      { label: '授权方式', value: '订阅授权' }
    ]
  }
])

// 根据ID获取当前插件
const plugin = computed(() => {
  return allPlugins.value.find(p => p.id === pluginId.value) || allPlugins.value[0]
})

// 导航菜单项
const navItems = ref([
  { label: '概述', key: 'overview' },
  { label: '功能特性', key: 'features' },
  { label: '应用场景', key: 'applications' },
  { label: '技术规格', key: 'specifications' },
  { label: '使用教程', key: 'tutorial' }
])

// 当前激活的导航项
const activeNav = ref('overview')

// 技术支持项
const supportItems = ref([
  { icon: markRaw(ChatDotRound), title: '在线客服' },
  { icon: markRaw(Phone), title: '电话支持' },
  { icon: markRaw(Document), title: '文档中心' },
  { icon: markRaw(VideoCamera), title: '视频教程' }
])

// 侧边栏粘性定位
const isSticky = ref(false)
const navMenu = ref(null)

// Intersection Observer 实例
let sectionObserver = null

// 初始化 Intersection Observer
const initIntersectionObserver = () => {
  const options = {
    root: null,
    rootMargin: '-20% 0px -70% 0px', // 触发区域
    threshold: 0
  }

  sectionObserver = new IntersectionObserver((entries) => {
    let visibleSection = null
    let maxRatio = 0

    entries.forEach(entry => {
      if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
        maxRatio = entry.intersectionRatio
        visibleSection = entry.target.id
      }
    })

    if (visibleSection) {
      activeNav.value = visibleSection
    }
  }, options)

  // 观察所有章节
  const sections = ['overview', 'features', 'applications', 'specifications']
  sections.forEach(sectionId => {
    const element = document.getElementById(sectionId)
    if (element) {
      sectionObserver.observe(element)
    }
  })
}

// 滚动到指定部分
const scrollToSection = (sectionId) => {
  if (sectionId === 'tutorial') {
    goToTutorial();
    return;
  }

  const element = document.getElementById(sectionId)
  if (element) {
    const offsetTop = element.offsetTop - 80
    window.scrollTo({
      top: offsetTop,
      behavior: 'smooth'
    })
  }

}

// 移动端适配
const isMobile = ref(false)

const checkIsMobile = () => {
  isMobile.value = window.innerWidth <= 768
}

onMounted(() => {
  // 验证插件ID是否存在
  // const pluginExists = allPlugins.value.some(p => p.id === pluginId.value)
  // if (!pluginExists) {
  //   console.log("插件不存在")
  //   // 重定向到首页
  //   router.push('/home')
  //   return
  // }

  // 初始化 Intersection Observer
  initIntersectionObserver()

  // 粘性导航逻辑
  window.addEventListener('scroll', handleScroll)

  // 移动端检测
  checkIsMobile()
  window.addEventListener('resize', checkIsMobile)
})

onBeforeUnmount(() => {
  // 清理 Intersection Observer
  if (sectionObserver) {
    sectionObserver.disconnect()
  }

  // 清理事件监听
  window.removeEventListener('scroll', handleScroll)
  window.removeEventListener('resize', checkIsMobile)
})

// 粘性导航逻辑
const handleScroll = () => {
  if (navMenu.value) {
    const navRect = navMenu.value.getBoundingClientRect()
    isSticky.value = navRect.top <= 0
  }
}
</script>

<style scoped>
.plugin-details-container {
  position: relative;
  min-height: 100vh;
  background: linear-gradient(135deg,
  rgba(54, 81, 191, 0.08) 0%,
  rgba(226, 103, 216, 0.06) 30%,
  rgba(235, 110, 13, 0.05) 70%,
  rgba(54, 81, 191, 0.04) 100%);
}

/* 技术支持菜单样式 */
.support-menu {
  position: fixed;
  right: 20px !important;
  top: 50% !important;
  transform: translateY(-50%);
  background: #fff;
  border-radius: 10px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12) !important;
  padding: 15px 10px !important;
  z-index: 1001;
}

.support-options {
  display: flex !important;
  flex-direction: column !important;
  gap: 20px !important;
}

.support-item {
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  cursor: pointer !important;
  padding: 8px 12px !important;
  border-radius: 6px !important;
  transition: all 0.3s !important;
  gap: 8px !important;
}

.support-item:hover {
  background: #f5f7fa !important;
  transform: translateY(-2px) !important;
}

.support-item .el-icon {
  font-size: 24px !important;
  color: #f54012 !important;
}

.support-title {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: #333 !important;
}

/* 顶部横幅样式 */
.top-banner {
  height: 60vh;
  min-height: 500px;
  background: linear-gradient(135deg, #0078d7 0%, #004e8c 50%, #ff8c00 100%);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.top-banner::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.1;
}

.banner-overlay {
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
  max-width: 800px;
  padding: 0 20px;
}

.plugin-title {
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #fff, #e0f7ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.plugin-subtitle {
  font-size: 1.3rem;
  margin-bottom: 2.5rem;
  opacity: 0.9;
  line-height: 1.6;
}

.banner-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.download-btn {
  background: linear-gradient(45deg, #ff8c00, #ff6b00);
  border: none;
  padding: 12px 30px;
  font-size: 1.1rem;
  border-radius: 25px;
  box-shadow: 0 4px 15px rgba(255, 140, 0, 0.3);
}

.learn-more-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 12px 30px;
  font-size: 1.1rem;
  border-radius: 25px;
  backdrop-filter: blur(10px);
}

/* 导航菜单样式 */
.nav-menu {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 1000;
  transition: all 0.3s ease;
}

.nav-menu.sticky {
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  height: 70px;
}

.nav-items {
  display: flex;
  gap: 2rem;
}

.nav-item {
  padding: 1rem 0;
  cursor: pointer;
  font-weight: 500;
  color: #64748b;
  position: relative;
  transition: color 0.3s ease;
}

.nav-item:hover,
.nav-item.active {
  color: #0078d7;
}

.nav-item.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(45deg, #0078d7, #ff8c00);
  border-radius: 2px;
}

.buy-now-btn {
  background: linear-gradient(45deg, #0078d7, #005a9e);
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-weight: 600;
}

/* 主要内容区域 */
.main-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 20px;
}

.content-section {
  margin-bottom: 80px;
  scroll-margin-top: 80px;
}

.section-header {
  text-align: center;
  margin-bottom: 3rem;
}

.section-header h2 {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: linear-gradient(135deg,
  #0078d7 0%,
  #6a5af9 25%,
  #d16ba5 50%,
  #ff8c00 75%,
  #ff6b00 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.section-divider {
  width: 80px;
  height: 4px;
  background: linear-gradient(45deg, #0078d7, #ff8c00);
  margin: 0 auto;
  border-radius: 2px;
}

/* 概述部分样式 */
.overview-content {
  display: flex;
  flex-direction: column;
  gap: 3rem;
}

.video-container {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.main-video {
  width: 100%;
  height: auto;
  display: block;
}

.overview-features {
  width: 100%;
}

.overview-features h3 {
  font-size: 1.8rem;
  margin-bottom: 2rem;
  color: #1e293b;
  margin-top: 0;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr); /* 一行两个 */
  gap: 1.5rem;
}

.feature-card {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.feature-icon {
  flex-shrink: 0;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg,
  #0078d7 0%,
  #6a5af9 50%,
  #ff8c00 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
}

.feature-content h4 {
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  color: #1e293b;
}

.feature-content p {
  color: #64748b;
  line-height: 1.6;
}

/* 功能特性详情样式 */
.feature-detail-item {
  margin-bottom: 4rem;
}

.feature-detail-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
}

.feature-detail-content.reverse {
  direction: rtl;
}

.feature-detail-content.reverse > * {
  direction: ltr;
}

.feature-text h3 {
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #1e293b;
}

.feature-text p {
  color: #64748b;
  line-height: 1.7;
  margin-bottom: 1.5rem;
}

.feature-text ul {
  list-style: none;
  padding: 0;
}

.feature-text li {
  padding: 0.5rem 0;
  color: #64748b;
  position: relative;
  padding-left: 1.5rem;
}

.feature-text li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #0078d7;
  font-weight: bold;
}

.feature-visual img {
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

/* 应用场景样式 */
.applications-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
}

.application-card {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.application-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.app-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg,
  #0078d7 0%,
  #6a5af9 50%,
  #ff8c00 100%);
  /* background: linear-gradient(135deg,
  #3498db 0%,
  #8e44ad 50%,
  #e74c3c 100%);*/
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2rem;
  margin: 0 auto 1.5rem;
}

.application-card h4 {
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: #1e293b;
}

.application-card p {
  color: #64748b;
  line-height: 1.6;
}

/* 技术规格样式 */
.specs-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: stretch;
}

.specs-list {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.spec-item {
  display: flex;
  justify-content: space-between;
  padding: 1.1rem 0;
  border-bottom: 1px solid #e2e8f0;
}

.spec-item:last-child {
  border-bottom: none;
}

.spec-label {
  font-weight: 600;
  color: #1e293b;
}

.spec-value {
  color: #64748b;
}

.specs-visual {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%; /* 确保高度100% */
}

.specs-visual img {
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

/* 侧边面板样式 */
.sidebar-panel {
  position: absolute;
  top: 60vh;
  right: 20px;
  width: 350px;
  transition: all 0.3s ease;
}

.sidebar-panel.sticky {
  position: fixed;
  top: 90px;
}

.purchase-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  margin-bottom: 1.5rem;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.card-header h3 {
  margin: 0;
  font-size: 1.3rem;
  color: #1e293b;
  flex: 1;
}

.plugin-tag {
  padding: 4px 12px;
  border-radius: 20px;
  color: white;
  font-size: 0.8rem;
  font-weight: 600;
  margin-left: 10px;
}

.rating-info {
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
}

.sales {
  margin-left: 1rem;
  color: #64748b;
  font-size: 0.9rem;
}

.price-section {
  margin-bottom: 1.5rem;
}

.current-price {
  font-size: 2rem;
  font-weight: 700;
  color: #f54012;
}

.purchase-options {
  margin-bottom: 1rem;
}

.purchase-btn {
  width: 100%;
  height: 50px;
  background: linear-gradient(45deg, #0078d7, #005a9e);
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.trial-option {
  text-align: center;
}

.trial-btn {
  width: 100%;
  height: 45px;
  border: 2px solid #0078d7;
  color: #0078d7;
  background: transparent;
  border-radius: 8px;
  font-weight: 600;
}

.plugin-meta {
  border-top: 1px solid #e2e8f0;
  padding-top: 1.5rem;
  margin-top: 1.5rem;
}

.meta-item {
  display: flex;
  align-items: center;
  margin-bottom: 0.8rem;
  color: #64748b;
  font-size: 0.9rem;
}

.meta-item .el-icon {
  margin-right: 0.5rem;
  color: #0078d7;
}

.support-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
}

.support-card h4 {
  margin: 0 0 1rem 0;
  color: #1e293b;
}

.support-options {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.support-item {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.8rem;
  border-radius: 8px;
  transition: background-color 0.3s ease;
  cursor: pointer;
}

.support-item:hover {
  background: #f8fafc;
}

.support-item .el-icon {
  color: #0078d7;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .sidebar-panel {
    position: static;
    width: 100%;
    max-width: 400px;
    margin: 2rem auto;
  }

  .main-content {
    padding-right: 20px;
    padding-left: 20px;
  }
}

@media (max-width: 768px) {
  .support-menu {
    display: none;
  }

  .plugin-title {
    font-size: 2.5rem;
  }

  .overview-content {
    gap: 2rem;
  }

  .features-grid {
    grid-template-columns: 1fr; /* 移动端改为单列 */
    gap: 1rem;
  }

  .feature-card {
    padding: 1rem;
  }

  .overview-features h3 {
    text-align: center; /* 移动端居中对齐 */
  }

  .feature-detail-content {
    grid-template-columns: 1fr;
  }

  .specs-content {
    grid-template-columns: 1fr;
  }

  .nav-container {
    flex-direction: column;
    height: auto;
    padding: 1rem 20px;
  }

  .nav-items {
    margin-bottom: 1rem;
    flex-wrap: wrap;
    justify-content: center;
  }

  .banner-actions {
    flex-direction: column;
    align-items: center;
  }

  .download-btn,
  .learn-more-btn {
    width: 100%;
    max-width: 250px;
  }
}

@media (max-width: 480px) {
  .plugin-title {
    font-size: 2rem;
  }

  .section-header h2 {
    font-size: 2rem;
  }

  .applications-grid {
    grid-template-columns: 1fr;
  }

  .feature-card {
    flex-direction: column;
    text-align: center;
  }
}
</style>

<style scoped>
/* 支付弹窗样式 */
.pay-dialog-content {
  text-align: center;
}

.product-info {
  margin-bottom: 20px;
}

.period-selection {
  margin: 20px 0;
}

.period-options {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-bottom: 20px;
}

.period-option {
  padding: 15px;
  border: 2px solid #e4e7ed;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  min-width: 80px;
}

.period-option:hover {
  border-color: #409eff;
}

.period-option.active {
  border-color: #409eff;
  background-color: #ecf5ff;
}

.period-days {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}

.period-price {
  font-size: 14px;
  color: #f56c6c;
  margin: 5px 0;
}

.period-desc {
  font-size: 12px;
  color: #909399;
}

.total-price {
  font-size: 18px;
  font-weight: bold;
  color: #f54012;
  margin-top: 15px;
}

.payment-methods {
  margin: 20px 0;
}

.payment-title {
  text-align: left;
  margin-bottom: 10px;
  font-weight: bold;
  color: #303133;
}

.wechat-payment {
  display: flex;
  align-items: center;
  padding: 12px;
  border: 2px solid #409eff;
  border-radius: 6px;
  background-color: #ecf5ff;
}

.wechat-payment .el-icon {
  color: #07c160;
  margin-right: 8px;
  font-size: 20px;
}

.qrcode-section {
  margin: 20px 0;
}

.qrcode-container {
  position: relative;
  display: inline-block;
  margin: 10px 0;
}

.qrcode-image {
  width: 200px;
  height: 200px;
  border: 1px solid #eee;
}

.success-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
}

.success-overlay p {
  margin-top: 10px;
  font-weight: bold;
  color: #52c41a;
}

.polling-hint {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
}

.polling-hint .el-icon {
  margin-right: 5px;
  animation: rotating 2s linear infinite;
}

.loading-qrcode {
  margin: 40px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #666;
}

.loading-qrcode .el-icon {
  font-size: 40px;
  color: #409eff;
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* 免费选项特殊样式 */
.free-option {
  background: linear-gradient(45deg, #52c41a, #73d13d) !important;
  color: white !important;
}

.free-option .period-days,
.free-option .period-free {
  color: white !important;
}

.period-free {
  font-size: 16px;
  font-weight: bold;
  margin: 5px 0;
}
</style>

<style scoped>
/* 周期选项增强样式 */
.period-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.period-option {
  position: relative;
  padding: 20px;
  border: 2px solid #e4e7ed;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
}

.period-option:hover {
  border-color: #409eff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.period-option.active {
  border-color: #409eff;
  background: linear-gradient(135deg, #ecf5ff, #f0f9ff);
}

.period-option.popular-option {
  border-color: #ff6b6b;
}

.period-badge {
  position: absolute;
  top: -10px;
  right: 16px;
  background: linear-gradient(45deg, #ff6b6b, #ff8e6b);
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
}

.period-header {
  text-align: center;
  margin-bottom: 16px;
}

.period-desc {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 4px;
}

.period-duration {
  font-size: 14px;
  color: #909399;
}

.price-section {
  text-align: center;
  margin-bottom: 16px;
}

.original-price {
  font-size: 14px;
  color: #909399;
  text-decoration: line-through;
  margin-bottom: 4px;
}

.current-price {
  font-size: 24px;
  font-weight: bold;
  color: #f56c6c;
  margin-bottom: 4px;
}

.free-price {
  font-size: 24px;
  font-weight: bold;
  color: #52c41a;
  margin-bottom: 4px;
}

.savings {
  font-size: 12px;
  color: #52c41a;
  background: #f6ffed;
  padding: 2px 8px;
  border-radius: 10px;
  display: inline-block;
}

.features-list {
  list-style: none;
  padding: 0;
  margin: 0 0 16px 0;
  text-align: left;
}

.features-list li {
  display: flex;
  align-items: center;
  padding: 6px 0;
  font-size: 13px;
  color: #606266;
}

.features-list .el-icon {
  color: #52c41a;
  margin-right: 8px;
  font-size: 14px;
}

.action-button {
  text-align: center;
  padding: 8px 0;
  background: #409eff;
  color: white;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.3s;
}

.period-option.active .action-button {
  background: #ff6b6b;
}

/* 免费选项特殊样式 */
.free-option {
  border-color: #52c41a;
}

.free-option.active {
  border-color: #52c41a;
  background: linear-gradient(135deg, #f6ffed, #f0fff3);
}

.free-option .action-button {
  background: #52c41a;
}

/* 支付区域样式 */
.payment-section {
  border-top: 1px solid #e4e7ed;
  padding-top: 20px;
  margin-top: 20px;
}

.selected-plan {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.selected-plan h4 {
  margin: 0 0 8px 0;
  color: #303133;
}

.total-amount {
  font-size: 20px;
  font-weight: bold;
  color: #f56c6c;
}

.payment-options {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.payment-option {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border: 2px solid #e4e7ed;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
}

.payment-option.active {
  border-color: #409eff;
  background: #ecf5ff;
}

.payment-option .el-icon {
  margin-right: 8px;
  font-size: 20px;
}

.wechat-payment .el-icon {
  color: #07c160;
}

.alipay-payment .el-icon {
  color: #1677ff;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .period-options {
    grid-template-columns: 1fr;
  }

  .payment-options {
    flex-direction: column;
  }
}
</style>